import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '#/components/page-header'
import {
  applicabilityConfigQueryOptions,
  applicabilityOptionsQueryOptions,
} from '#/api/applicability'
import { questionnaireQueryOptions } from '#/api/questionnaires'
import { ApplicabilityForm } from '#/components/questionnaire/applicability-form'
import { EditionLine } from '#/components/questionnaire/edition-line'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { formatCode } from '#/lib/format'
import { hasPermission, useCan } from '#/user/me'
import { PageContainer } from '#/components/page-container'

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/$workspaceId/settings',
)({
  loader: async ({ context, params }) => {
    const questionnaireId = context.workspace.questionnaire_id
    if (!questionnaireId) return
    const { queryClient } = context
    const ids = [params.accountId, params.workspaceId, questionnaireId] as const
    // The edition is read-only, so everyone who can open the page sees it. It
    // needs only the questionnaire's code, so it asks for no includes.
    const loads: Array<Promise<unknown>> = [
      queryClient.ensureQueryData(questionnaireQueryOptions(...ids)),
    ]
    // Applicability config is admin-scoped — only the gated form reads it, so
    // skip that prefetch for members without workspaces:configure (they see
    // the no-access notice).
    if (hasPermission(context.me, 'annwn:workspaces:configure')) {
      loads.push(
        queryClient.ensureQueryData(applicabilityConfigQueryOptions(...ids)),
        queryClient.ensureQueryData(applicabilityOptionsQueryOptions(...ids)),
      )
    }
    await Promise.all(loads)
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: WorkspaceSettings,
})

function WorkspaceSettings() {
  const { t } = useTranslation()
  const { accountId, workspaceId } = Route.useParams()
  const { workspace } = Route.useRouteContext()
  const canConfigure = useCan('annwn:workspaces:configure')

  return (
    <PageContainer>
      <PageHeader
        title={t('workspace.applicability.title')}
        description={
          workspace.questionnaire_id ? (
            <WorkspaceEdition
              accountId={accountId}
              workspaceId={workspaceId}
              questionnaireId={workspace.questionnaire_id}
            />
          ) : undefined
        }
      />

      {!canConfigure ? (
        <p className="text-sm text-muted-foreground">
          {t('workspace.applicability.noAccess')}
        </p>
      ) : workspace.questionnaire_id ? (
        <ApplicabilityForm
          accountId={accountId}
          workspaceId={workspaceId}
          questionnaireId={workspace.questionnaire_id}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('workspace.applicability.noQuestionnaire')}
        </p>
      )}
    </PageContainer>
  )
}

// The edition is the questionnaire the workspace was created from, named by its
// code as the workspace overview names it.
function WorkspaceEdition({
  accountId,
  workspaceId,
  questionnaireId,
}: {
  accountId: string
  workspaceId: string
  questionnaireId: string
}) {
  const { data: questionnaire } = useSuspenseQuery(
    questionnaireQueryOptions(accountId, workspaceId, questionnaireId),
  )
  return <EditionLine edition={formatCode(questionnaire.code)} />
}
